// src/types/index.ts
var AddressType = {
  BILLING: "billing",
  SHIPPING: "shipping"
};
var InvoiceStatus = {
  DRAFT: "draft",
  SENT: "sent",
  PAID: "paid",
  OVERDUE: "overdue",
  CANCELLED: "cancelled"
};
var UnitType = {
  HOURS: "hours",
  DAYS: "days",
  PIECES: "pieces",
  FLAT_RATE: "flat_rate",
  KM: "km"
};
var DiscountType = {
  PERCENT: "percent",
  ABSOLUTE: "absolute"
};
var BillingInterval = {
  MONTHLY: "monthly",
  QUARTERLY: "quarterly",
  SEMI_ANNUAL: "semi_annual",
  ANNUAL: "annual"
};
var EstimateStatus = {
  DRAFT: "draft",
  SENT: "sent",
  ACCEPTED: "accepted",
  REJECTED: "rejected",
  EXPIRED: "expired",
  CONVERTED: "converted"
};

// src/validation/common.ts
import { z } from "zod";
var uuidSchema = z.string().uuid();
var isoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Invalid date format (YYYY-MM-DD)");
var isoDateTimeSchema = z.string().datetime();
var paginationQuerySchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().min(1).max(200).default(40)
});
var searchQuerySchema = z.object({
  q: z.string().optional()
});
var addressTypeSchema = z.enum(["billing", "shipping"]);
var invoiceStatusSchema = z.enum(["draft", "sent", "paid", "overdue", "cancelled", "deferred"]);
var unitTypeSchema = z.enum(["hours", "days", "pieces", "flat_rate", "km"]);
var discountTypeSchema = z.enum(["percent", "absolute"]);
var billingIntervalSchema = z.enum(["monthly", "quarterly", "semi_annual", "annual"]);
var estimateStatusSchema = z.enum([
  "draft",
  "sent",
  "accepted",
  "rejected",
  "expired",
  "converted"
]);
var loginSchema = z.object({
  username: z.string().min(1, "Username is required"),
  password: z.string().min(1, "Password is required")
});
var versionSchema = z.object({
  version: z.number().int().positive()
});
var vatRateSchema = z.number().refine(
  (val) => [0, 7, 19].includes(val),
  { message: "VAT rate must be 0%, 7%, or 19%" }
);
var moneySchema = z.number().multipleOf(0.01);
var positiveMoneySchema = moneySchema.positive();
var nonNegativeMoneySchema = moneySchema.nonnegative();

// src/validation/customer.ts
import { z as z2 } from "zod";
var createCustomerSchema = z2.object({
  companyName: z2.string().min(1, "Firmenname ist erforderlich").max(255),
  contactFirstName: z2.string().max(100).optional(),
  contactLastName: z2.string().max(100).optional(),
  email: z2.string().email("Ung\xFCltige E-Mail-Adresse").max(255).optional().or(z2.literal("")),
  phone: z2.string().max(50).optional(),
  defaultPaymentDays: z2.number().int().min(0).max(365).optional(),
  notes: z2.string().optional()
});
var updateCustomerSchema = z2.object({
  companyName: z2.string().min(1, "Firmenname ist erforderlich").max(255).optional(),
  contactFirstName: z2.string().max(100).nullable().optional(),
  contactLastName: z2.string().max(100).nullable().optional(),
  email: z2.string().email("Ung\xFCltige E-Mail-Adresse").max(255).nullable().optional().or(z2.literal("")),
  phone: z2.string().max(50).nullable().optional(),
  defaultPaymentDays: z2.number().int().min(0).max(365).nullable().optional(),
  notes: z2.string().nullable().optional(),
  version: z2.number().int().positive("Version ist erforderlich")
});
var archiveCustomerSchema = z2.object({
  isArchived: z2.boolean(),
  version: z2.number().int().positive()
});
var customerListQuerySchema = paginationQuerySchema.merge(searchQuerySchema).extend({
  isArchived: z2.preprocess(
    (val) => val === "true" || val === true,
    z2.boolean()
  ).optional(),
  sortBy: z2.enum(["companyName", "customerNumber", "createdAt", "updatedAt"]).optional().default("companyName"),
  sortOrder: z2.enum(["asc", "desc"]).optional().default("asc")
});
var customerIdParamSchema = z2.object({
  id: uuidSchema
});

// src/validation/address.ts
import { z as z3 } from "zod";
var createAddressSchema = z3.object({
  addressType: addressTypeSchema,
  isDefault: z3.boolean().optional().default(false),
  street: z3.string().min(1, "Stra\xDFe ist erforderlich").max(255),
  street2: z3.string().max(255).optional(),
  postalCode: z3.string().min(1, "PLZ ist erforderlich").max(20),
  city: z3.string().min(1, "Stadt ist erforderlich").max(100),
  country: z3.string().length(2, "L\xE4ndercode muss 2 Zeichen haben").optional().default("DE")
});
var updateAddressSchema = z3.object({
  addressType: addressTypeSchema.optional(),
  isDefault: z3.boolean().optional(),
  street: z3.string().min(1, "Stra\xDFe ist erforderlich").max(255).optional(),
  street2: z3.string().max(255).nullable().optional(),
  postalCode: z3.string().min(1, "PLZ ist erforderlich").max(20).optional(),
  city: z3.string().min(1, "Stadt ist erforderlich").max(100).optional(),
  country: z3.string().length(2, "L\xE4ndercode muss 2 Zeichen haben").optional()
});
var addressIdParamSchema = z3.object({
  id: uuidSchema
});
var customerAddressParamSchema = z3.object({
  customerId: uuidSchema
});
var addressWithCustomerParamSchema = z3.object({
  customerId: uuidSchema,
  addressId: uuidSchema
});

// src/validation/invoice.ts
import { z as z4 } from "zod";
var billingAddressSnapshotSchema = z4.object({
  companyName: z4.string(),
  contactFirstName: z4.string().optional(),
  contactLastName: z4.string().optional(),
  street: z4.string(),
  street2: z4.string().optional(),
  postalCode: z4.string(),
  city: z4.string(),
  country: z4.string().length(2).default("DE")
});
var createInvoiceSchema = z4.object({
  customerId: uuidSchema,
  billingAddressId: uuidSchema.optional(),
  invoiceDate: isoDateSchema.optional(),
  // Defaults to today
  dueDate: isoDateSchema.optional(),
  // Can be auto-calculated from customer payment days
  vatRate: vatRateSchema.optional().default(19),
  overallDiscountPercent: z4.number().min(0).max(100).optional(),
  notes: z4.string().optional(),
  internalNotes: z4.string().optional(),
  showInternalNotesOnPdf: z4.boolean().optional(),
  customerReference: z4.string().max(100).optional()
});
var updateInvoiceSchema = z4.object({
  billingAddressId: uuidSchema.nullable().optional(),
  invoiceDate: isoDateSchema.optional(),
  dueDate: isoDateSchema.optional(),
  vatRate: vatRateSchema.optional(),
  overallDiscountPercent: z4.number().min(0).max(100).nullable().optional(),
  notes: z4.string().nullable().optional(),
  internalNotes: z4.string().nullable().optional(),
  showInternalNotesOnPdf: z4.boolean().optional(),
  customerReference: z4.string().max(100).nullable().optional(),
  version: z4.number().int().positive("Version ist erforderlich")
});
var invoiceDraftItemSchema = z4.object({
  itemId: uuidSchema.optional(),
  description: z4.string().min(1, "Beschreibung ist erforderlich"),
  quantity: z4.number().positive("Menge muss positiv sein").default(1),
  unitType: unitTypeSchema.default("pieces"),
  unitPrice: moneySchema,
  discountType: discountTypeSchema.nullable().optional(),
  discountValue: nonNegativeMoneySchema.nullable().optional()
}).refine(
  (item) => {
    if (!item.discountType) return item.discountValue == null;
    if (item.discountValue == null) return false;
    return item.discountType !== "percent" || item.discountValue <= 100;
  },
  {
    message: "Prozentrabatt muss zwischen 0 und 100 liegen"
  }
);
var createInvoiceDraftSchema = z4.object({
  invoice: createInvoiceSchema,
  items: z4.array(invoiceDraftItemSchema).min(1, "Mindestens eine Position erforderlich")
});
var updateInvoiceDraftSchema = z4.object({
  invoice: updateInvoiceSchema,
  items: z4.array(invoiceDraftItemSchema).min(1, "Mindestens eine Position erforderlich")
});
var markInvoicePaidSchema = z4.object({
  paymentDate: isoDateSchema.optional(),
  // Defaults to today
  version: z4.number().int().positive()
});
var updateInvoicePaymentDateSchema = z4.object({
  paymentDate: isoDateSchema,
  version: z4.number().int().positive()
});
var markInvoiceSentSchema = z4.object({
  version: z4.number().int().positive()
});
var cancelInvoiceSchema = z4.object({
  notes: z4.string().optional(),
  version: z4.number().int().positive()
});
var markInvoiceDeferredSchema = z4.object({
  version: z4.number().int().positive()
});
var unDeferInvoiceSchema = z4.object({
  version: z4.number().int().positive()
});
var updateInvoiceNotesSchema = z4.object({
  internalNotes: z4.string().nullable().optional(),
  showInternalNotesOnPdf: z4.boolean().optional()
});
var invoiceListQuerySchema = paginationQuerySchema.merge(searchQuerySchema).extend({
  customerId: uuidSchema.optional(),
  status: z4.union([invoiceStatusSchema, z4.array(invoiceStatusSchema)]).optional(),
  dateBasis: z4.enum(["invoiceDate", "paymentDate"]).optional(),
  paymentStatus: z4.enum(["all", "known", "unknown"]).optional(),
  dateFrom: isoDateSchema.optional(),
  dateTo: isoDateSchema.optional(),
  overdue: z4.preprocess((val) => val === "true" || val === true, z4.boolean()).optional(),
  sortBy: z4.enum([
    "invoiceNumber",
    "invoiceDate",
    "dueDate",
    "grossTotal",
    "status",
    "createdAt",
    "paymentDate"
  ]).optional().default("invoiceDate"),
  sortOrder: z4.enum(["asc", "desc"]).optional().default("desc")
});
var invoiceIdParamSchema = z4.object({
  id: uuidSchema
});

// src/validation/invoice-item.ts
import { z as z5 } from "zod";
var createInvoiceItemSchema = z5.object({
  description: z5.string().min(1, "Beschreibung ist erforderlich"),
  quantity: z5.number().positive("Menge muss positiv sein").default(1),
  unitType: unitTypeSchema.default("pieces"),
  unitPrice: moneySchema,
  // Allows negative for discount/rebate lines
  discountType: discountTypeSchema.optional(),
  discountValue: nonNegativeMoneySchema.optional()
}).refine(
  (data) => {
    if (data.discountType && data.discountValue === void 0) {
      return false;
    }
    if (data.discountType === "percent" && data.discountValue !== void 0) {
      return data.discountValue >= 0 && data.discountValue <= 100;
    }
    return true;
  },
  {
    message: "Ung\xFCltiger Rabatt"
  }
);
var updateInvoiceItemSchema = z5.object({
  description: z5.string().min(1, "Beschreibung ist erforderlich").optional(),
  quantity: z5.number().positive("Menge muss positiv sein").optional(),
  unitType: unitTypeSchema.optional(),
  unitPrice: moneySchema.optional(),
  discountType: discountTypeSchema.nullable().optional(),
  discountValue: nonNegativeMoneySchema.nullable().optional()
}).refine(
  (data) => {
    if (data.discountType === "percent" && data.discountValue !== void 0 && data.discountValue !== null) {
      return data.discountValue >= 0 && data.discountValue <= 100;
    }
    return true;
  },
  {
    message: "Prozentrabatt muss zwischen 0 und 100 liegen"
  }
);
var reorderInvoiceItemsSchema = z5.object({
  itemIds: z5.array(uuidSchema).min(1, "Mindestens eine Position erforderlich")
});
var invoiceItemIdParamSchema = z5.object({
  invoiceId: uuidSchema,
  itemId: uuidSchema
});

// src/validation/reminder.ts
import { z as z6 } from "zod";
var reminderLevelConfigSchema = z6.object({
  level: z6.number().int().min(1).max(3),
  defaultFee: z6.number().min(0).default(0),
  defaultDueDays: z6.number().int().min(1).default(14),
  defaultInterestRate: z6.number().min(0).max(100).optional()
});
var createReminderSchema = z6.object({
  invoiceId: uuidSchema,
  reminderDate: isoDateSchema.optional(),
  // Defaults to today
  dueDate: isoDateSchema.optional(),
  // Auto-calculated if not provided
  reminderFee: z6.number().min(0).optional().default(0),
  interestRate: z6.number().min(0).max(100).optional(),
  notes: z6.string().optional()
});
var updateReminderSchema = z6.object({
  dueDate: isoDateSchema.optional(),
  reminderFee: z6.number().min(0).optional(),
  interestRate: z6.number().min(0).max(100).optional(),
  notes: z6.string().nullable().optional()
});
var reminderListQuerySchema = paginationQuerySchema.extend({
  invoiceId: uuidSchema.optional(),
  level: z6.coerce.number().int().min(1).optional()
});
var reminderIdParamSchema = z6.object({
  id: uuidSchema
});
var invoiceReminderParamSchema = z6.object({
  invoiceId: uuidSchema
});

// src/validation/contract.ts
import { z as z7 } from "zod";
var contractStatusSchema = z7.enum(["draft", "active", "paused", "completed", "cancelled"]);
var contractBillingIntervalSchema = z7.enum([
  "monthly",
  "quarterly",
  "semiannually",
  "annually"
]);
var contractItemInputSchema = z7.object({
  id: z7.string().uuid().optional(),
  position: z7.number().int().positive(),
  description: z7.string().min(1).max(500),
  quantity: z7.number().positive().default(1),
  unit: z7.string().max(20).default("St\xFCck"),
  unitPrice: z7.number().nonnegative(),
  longDescription: z7.string().optional().nullable()
});
var createContractSchema = z7.object({
  title: z7.string().min(1, "Titel ist erforderlich").max(255),
  description: z7.string().optional().nullable(),
  customerId: z7.string().uuid("Ung\xFCltige Kunden-ID"),
  startDate: z7.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Ung\xFCltiges Datum"),
  endDate: z7.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional().nullable(),
  billingInterval: contractBillingIntervalSchema.default("monthly"),
  billingDayOfMonth: z7.number().int().min(1).max(28).default(1),
  vatRate: z7.number().min(0).max(100).default(19),
  customerReference: z7.string().max(100).optional().nullable(),
  invoicePrefix: z7.string().max(20).optional().nullable(),
  autoGenerate: z7.boolean().default(true),
  items: z7.array(contractItemInputSchema).min(1, "Mindestens eine Position erforderlich")
});
var updateContractSchema = z7.object({
  version: z7.number().int().positive().optional(),
  title: z7.string().min(1).max(255).optional(),
  description: z7.string().optional().nullable(),
  customerId: z7.string().uuid().optional(),
  startDate: z7.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  endDate: z7.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional().nullable(),
  billingInterval: contractBillingIntervalSchema.optional(),
  billingDayOfMonth: z7.number().int().min(1).max(28).optional(),
  vatRate: z7.number().min(0).max(100).optional(),
  customerReference: z7.string().max(100).optional().nullable(),
  invoicePrefix: z7.string().max(20).optional().nullable(),
  autoGenerate: z7.boolean().optional(),
  items: z7.array(contractItemInputSchema).min(1, "Mindestens eine Position erforderlich").optional()
});
var contractWithRelationsSchema = z7.object({
  id: z7.string().uuid(),
  contractNumber: z7.string(),
  title: z7.string(),
  description: z7.string().nullable(),
  customerId: z7.string().uuid(),
  status: contractStatusSchema,
  startDate: z7.string(),
  endDate: z7.string().nullable(),
  terminationDate: z7.string().nullable(),
  billingInterval: contractBillingIntervalSchema,
  billingDayOfMonth: z7.number(),
  nextBillingDate: z7.string().nullable(),
  lastBillingDate: z7.string().nullable(),
  vatRate: z7.string(),
  customerReference: z7.string().nullable(),
  invoicePrefix: z7.string().nullable(),
  autoGenerate: z7.boolean(),
  createdAt: z7.string(),
  updatedAt: z7.string(),
  version: z7.number(),
  customer: z7.object({
    id: z7.string().uuid(),
    customerNumber: z7.string(),
    companyName: z7.string()
  }).optional(),
  items: z7.array(contractItemInputSchema)
});
var listContractsQuerySchema = z7.object({
  page: z7.coerce.number().int().positive().default(1),
  limit: z7.coerce.number().int().positive().max(100).default(20),
  status: contractStatusSchema.optional(),
  customerId: z7.string().uuid().optional(),
  search: z7.string().optional(),
  sortBy: z7.enum(["contractNumber", "title", "startDate", "nextBillingDate", "createdAt"]).default("createdAt"),
  sortDirection: z7.enum(["asc", "desc"]).default("desc")
});
var contractBillingIntervalLabels = {
  monthly: "Monatlich",
  quarterly: "Viertelj\xE4hrlich",
  semiannually: "Halbj\xE4hrlich",
  annually: "J\xE4hrlich"
};
var contractBillingIntervalMonths = {
  monthly: 1,
  quarterly: 3,
  semiannually: 6,
  annually: 12
};
var contractStatusLabels = {
  draft: "Entwurf",
  active: "Aktiv",
  paused: "Pausiert",
  completed: "Abgeschlossen",
  cancelled: "Storniert"
};
var contractStatusColors = {
  draft: "bg-white/10 text-white/60",
  active: "bg-green-500/20 text-green-400",
  paused: "bg-yellow-500/20 text-yellow-400",
  completed: "bg-blue-500/20 text-blue-400",
  cancelled: "bg-red-500/20 text-red-400"
};

// src/validation/estimate.ts
import { z as z8 } from "zod";
var estimateStatusLabels = {
  draft: "Entwurf",
  sent: "Versendet",
  accepted: "Angenommen",
  rejected: "Abgelehnt",
  expired: "Abgelaufen",
  converted: "Umgewandelt"
};
var estimateStatusColors = {
  draft: "bg-gray-500/20 text-gray-400",
  sent: "bg-blue-500/20 text-blue-400",
  accepted: "bg-green-500/20 text-green-400",
  rejected: "bg-red-500/20 text-red-400",
  expired: "bg-orange-500/20 text-orange-400",
  converted: "bg-purple-500/20 text-purple-400"
};
var estimateItemInputSchema = z8.object({
  position: z8.number().int().min(1),
  description: z8.string().min(1).max(500),
  longDescription: z8.string().max(2e3).optional(),
  quantity: z8.number().positive(),
  unit: z8.string().max(20).default("St\xFCck"),
  unitPrice: z8.number().nonnegative()
});
var createEstimateSchema = z8.object({
  customerId: z8.string().uuid(),
  issueDate: z8.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  validUntil: z8.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  subject: z8.string().max(255).optional(),
  customerReference: z8.string().max(100).optional(),
  introText: z8.string().max(2e3).optional(),
  closingText: z8.string().max(2e3).optional(),
  vatRate: z8.number().min(0).max(100).default(19),
  items: z8.array(estimateItemInputSchema).min(1)
});
var updateEstimateSchema = z8.object({
  customerId: z8.string().uuid().optional(),
  version: z8.number().int().positive().optional(),
  issueDate: z8.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  validUntil: z8.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
  subject: z8.string().max(255).nullable().optional(),
  customerReference: z8.string().max(100).nullable().optional(),
  introText: z8.string().max(2e3).nullable().optional(),
  closingText: z8.string().max(2e3).nullable().optional(),
  vatRate: z8.number().min(0).max(100).optional(),
  items: z8.array(estimateItemInputSchema).min(1).optional()
});
var listEstimatesQuerySchema = z8.object({
  page: z8.coerce.number().int().min(1).default(1),
  limit: z8.coerce.number().int().min(1).max(100).default(20),
  status: estimateStatusSchema.optional(),
  customerId: z8.string().uuid().optional(),
  search: z8.string().optional(),
  sortBy: z8.enum(["issueDate", "estimateNumber", "grossTotal", "validUntil"]).default("issueDate"),
  sortOrder: z8.enum(["asc", "desc"]).default("desc")
});

// src/validation/audit.ts
import { z as z9 } from "zod";
var auditEntityIdSchema = uuidSchema;
var auditEntityTypeSchema = z9.enum([
  "user",
  "customer",
  "address",
  "invoice",
  "invoice_item",
  "payment_reminder",
  "contract",
  "contract_item",
  "cost_estimate",
  "estimate_item",
  "settings"
]);
var auditActionSchema = z9.enum(["create", "update", "delete"]);
var auditListQuerySchema = paginationQuerySchema.extend({
  entityType: auditEntityTypeSchema.optional(),
  entityId: auditEntityIdSchema.optional(),
  action: auditActionSchema.optional(),
  userId: z9.string().optional(),
  dateFrom: isoDateSchema.optional(),
  dateTo: isoDateSchema.optional()
});
var auditEntityParamSchema = z9.object({
  entityType: auditEntityTypeSchema,
  entityId: auditEntityIdSchema
});
var auditIdParamSchema = z9.object({
  id: z9.string().uuid()
});

// src/validation/supplier.ts
import { z as z10 } from "zod";
var addressSnapshotSchema = z10.object({
  street: z10.string().min(1),
  street2: z10.string().optional().nullable(),
  postalCode: z10.string().min(1),
  city: z10.string().min(1),
  country: z10.string().length(2).default("DE")
});
var createSupplierSchema = z10.object({
  displayName: z10.string().min(1).max(255),
  vatId: z10.string().max(20).optional().nullable(),
  taxNumber: z10.string().max(50).optional().nullable(),
  addressSnapshot: addressSnapshotSchema.optional().nullable(),
  defaultEmailDomain: z10.string().max(255).optional().nullable(),
  additionalAllowedEmails: z10.array(z10.string().email()).default([]),
  // T571b — IBAN/BIC accepted on create+update so the supplier-detail
  // edit-form can persist payment routing surfaced from GiroCode scans.
  iban: z10.string().max(34).optional().nullable(),
  bic: z10.string().max(11).optional().nullable(),
  kleinunternehmer: z10.boolean().default(false),
  defaultAccountOverride: z10.string().max(10).optional().nullable(),
  defaultCategoryId: z10.string().uuid().optional().nullable(),
  defaultPaymentDays: z10.number().int().positive().optional().nullable(),
  notes: z10.string().optional().nullable()
});
var updateSupplierSchema = createSupplierSchema.partial().extend({
  version: z10.number().int().positive()
});
var supplierListQuerySchema = paginationQuerySchema.merge(searchQuerySchema).extend({
  includeArchived: z10.coerce.boolean().default(false)
});

// src/validation/expense-category.ts
import { z as z11 } from "zod";
var accountingClassSchema = z11.enum(["sammelposten", "abschreibung"]);
var createExpenseCategorySchema = z11.object({
  name: z11.string().min(1).max(100),
  accountingClass: accountingClassSchema,
  topic: z11.string().min(1).max(100),
  deductiblePercent: z11.number().min(0).max(100).default(100),
  depreciationYears: z11.number().int().positive().optional().nullable(),
  displayOrder: z11.number().int().default(0)
}).superRefine((v, ctx) => {
  if (v.accountingClass === "abschreibung" && !v.depreciationYears) {
    ctx.addIssue({
      code: "custom",
      path: ["depreciationYears"],
      message: "depreciationYears ist Pflicht bei accounting_class=abschreibung"
    });
  }
});
var updateExpenseCategorySchema = z11.object({
  name: z11.string().min(1).max(100).optional(),
  accountingClass: accountingClassSchema.optional(),
  topic: z11.string().min(1).max(100).optional(),
  deductiblePercent: z11.number().min(0).max(100).optional(),
  depreciationYears: z11.number().int().positive().optional().nullable(),
  displayOrder: z11.number().int().optional(),
  isArchived: z11.boolean().optional(),
  version: z11.number().int().positive()
});
var categoryListQuerySchema = paginationQuerySchema.merge(searchQuerySchema).extend({
  accountingClass: accountingClassSchema.optional(),
  includeArchived: z11.coerce.boolean().default(false)
});

// src/validation/expense-item.ts
import { z as z12 } from "zod";
var expenseItemInputSchema = z12.object({
  position: z12.number().int().positive(),
  description: z12.string().min(1),
  quantity: z12.number().positive().default(1),
  unit: z12.string().max(20).optional().nullable(),
  unitPriceNet: z12.number(),
  vatRateAtSave: z12.number().min(0).max(30)
  // Accept any VAT rate; tenant_vat_settings constrains
});

// src/validation/expense.ts
import { z as z13 } from "zod";
var expenseTypeSchema = z13.enum([
  "invoice",
  "credit_note",
  "refund",
  "advance_payment",
  "final_invoice"
]);
var expenseStatusSchema = z13.enum([
  "draft",
  "neu",
  "gepr\xFCft",
  "r\xFCckfrage_offen",
  "nicht_relevant",
  "zur\xFCckgezogen",
  "archiviert"
]);
var expenseSourceSchema = z13.enum(["manual", "email", "drive", "erechnung", "ki"]);
var createExpenseSchema = z13.object({
  supplierId: uuidSchema.optional().nullable(),
  categoryId: uuidSchema.optional().nullable(),
  supplierInvoiceNumber: z13.string().max(100).optional().nullable(),
  customerReference: z13.string().max(100).optional().nullable(),
  type: expenseTypeSchema.default("invoice"),
  reversesExpenseId: uuidSchema.optional().nullable(),
  referencesAdvanceIds: z13.array(uuidSchema).default([]),
  invoiceDate: isoDateSchema,
  dueDate: isoDateSchema.optional().nullable(),
  paymentDate: isoDateSchema.optional().nullable(),
  currency: z13.string().length(3).default("EUR"),
  reverseCharge: z13.boolean().default(false),
  supplierKleinunternehmer: z13.boolean().default(false),
  isKleinbetragsrechnung: z13.boolean().default(false),
  privateUseOverridePercent: z13.number().min(0).max(100).optional().nullable(),
  privateUseReason: z13.string().optional().nullable(),
  notes: z13.string().optional().nullable(),
  internalNotes: z13.string().optional().nullable(),
  items: z13.array(expenseItemInputSchema).default([]),
  confirmNachtr\u00E4glich: z13.boolean().default(false),
  asDraft: z13.boolean().default(false)
  // client hint — force `draft` status
}).superRefine((v, ctx) => {
  if (v.privateUseOverridePercent !== null && v.privateUseOverridePercent !== void 0) {
    if (!v.privateUseReason || v.privateUseReason.trim().length < 10) {
      ctx.addIssue({
        code: "custom",
        path: ["privateUseReason"],
        message: "Begr\xFCndung (\u2265 10 Zeichen) Pflicht bei private_use_override_percent"
      });
    }
  }
  if ((v.type === "credit_note" || v.type === "refund") && !v.reversesExpenseId) {
    ctx.addIssue({
      code: "custom",
      path: ["reversesExpenseId"],
      message: "reverses_expense_id Pflicht bei Gutschrift/Erstattung"
    });
  }
  if (v.type === "final_invoice" && v.referencesAdvanceIds.length === 0) {
    ctx.addIssue({
      code: "custom",
      path: ["referencesAdvanceIds"],
      message: "Mindestens eine Anzahlungs-Referenz Pflicht bei Schlussrechnung"
    });
  }
});
var updateExpenseSchema = z13.object({
  supplierId: uuidSchema.optional().nullable(),
  categoryId: uuidSchema.optional().nullable(),
  supplierInvoiceNumber: z13.string().max(100).optional().nullable(),
  customerReference: z13.string().max(100).optional().nullable(),
  type: expenseTypeSchema.optional(),
  invoiceDate: isoDateSchema.optional(),
  dueDate: isoDateSchema.optional().nullable(),
  paymentDate: isoDateSchema.optional().nullable(),
  reverseCharge: z13.boolean().optional(),
  supplierKleinunternehmer: z13.boolean().optional(),
  isKleinbetragsrechnung: z13.boolean().optional(),
  privateUseOverridePercent: z13.number().min(0).max(100).optional().nullable(),
  privateUseReason: z13.string().optional().nullable(),
  notes: z13.string().optional().nullable(),
  internalNotes: z13.string().optional().nullable(),
  items: z13.array(expenseItemInputSchema).optional(),
  // T457 (FR-051d): explicit confirmation required when an update moves
  // invoice_date into a closed quartal. Plain optional (no default) so
  // existing call sites that don't pass it stay type-compatible — the
  // service layer enforces by throwing ConflictError when undefined.
  confirmNachtr\u00E4glich: z13.boolean().optional(),
  // T-G2-fe (Phase-17, FR-G2, codex P1) — Sum-Validation-Override Reason.
  // Wenn das Frontend ein Posten-Update mit divergenter Σ(items) vs.
  // declared gross schickt, muss eine ≥10-Zeichen-Begründung mitkommen.
  // Service-Layer schreibt eine `manual` Revision + audit-event mit
  // diesem Reason als Trail (FR-005e-Pattern reuse).
  sumValidationOverrideReason: z13.string().min(10).max(500).optional(),
  version: z13.number().int().positive()
}).superRefine((v, ctx) => {
  if (v.privateUseOverridePercent !== null && v.privateUseOverridePercent !== void 0 && (!v.privateUseReason || v.privateUseReason.trim().length < 10)) {
    ctx.addIssue({
      code: "custom",
      path: ["privateUseReason"],
      message: "Begr\xFCndung (\u2265 10 Zeichen) Pflicht bei private_use_override_percent"
    });
  }
});
var expenseStatusTransitionSchema = z13.object({
  toStatus: expenseStatusSchema,
  reason: z13.string().optional().nullable(),
  version: z13.number().int().positive()
});
var quartalSchema = z13.string().regex(/^\d{4}-Q[1-4]$/, "Quartal muss `YYYY-Q1..Q4` sein, z.B. `2026-Q1`");
var expenseListQuerySchema = paginationQuerySchema.merge(searchQuerySchema).extend({
  status: z13.union([expenseStatusSchema, z13.array(expenseStatusSchema)]).optional().transform((v) => Array.isArray(v) ? v : v ? [v] : void 0),
  categoryId: z13.union([uuidSchema, z13.array(uuidSchema)]).optional().transform((v) => Array.isArray(v) ? v : v ? [v] : void 0),
  supplierId: uuidSchema.optional(),
  source: z13.union([expenseSourceSchema, z13.array(expenseSourceSchema)]).optional().transform((v) => Array.isArray(v) ? v : v ? [v] : void 0),
  dateBasis: z13.enum(["invoiceDate", "paymentDate"]).optional(),
  paymentStatus: z13.enum(["all", "known", "unknown"]).optional(),
  dateFrom: isoDateSchema.optional(),
  dateTo: isoDateSchema.optional(),
  // `quartal=2026-Q1` is a high-level filter; the server expands it into
  // `dateFrom/dateTo` (start_of_quarter..end_of_quarter on invoice_date).
  // dateFrom/dateTo still win if both are set — quartal is the convenience,
  // dateFrom/dateTo is the override per T489.
  quartal: quartalSchema.optional(),
  // T527 — "Nur meine Uploads" quick-toggle. Server intersects with
  // `expenses.created_by_user_id` from the session. Boolean coerce so a
  // URL `?mine=true` parses to `true`.
  mine: z13.coerce.boolean().optional()
});
var assignCategorySchema = z13.object({
  categoryId: uuidSchema,
  version: z13.number().int().positive()
});

// src/validation/bank.ts
import { z as z14 } from "zod";

// src/utils/iban.ts
function normalizeIban(value) {
  return value.replace(/\s+/g, "").toUpperCase();
}
var IBAN_LENGTHS = {
  AD: 24,
  AE: 23,
  AL: 28,
  AT: 20,
  AZ: 28,
  BA: 20,
  BE: 16,
  BG: 22,
  BH: 22,
  BR: 29,
  BY: 28,
  CH: 21,
  CR: 22,
  CY: 28,
  CZ: 24,
  DE: 22,
  DK: 18,
  DO: 28,
  EE: 20,
  EG: 29,
  ES: 24,
  FI: 18,
  FO: 18,
  FR: 27,
  GB: 22,
  GE: 22,
  GI: 23,
  GL: 18,
  GR: 27,
  GT: 28,
  HR: 21,
  HU: 28,
  IE: 22,
  IL: 23,
  IQ: 23,
  IS: 26,
  IT: 27,
  JO: 30,
  KW: 30,
  KZ: 20,
  LB: 28,
  LC: 32,
  LI: 21,
  LT: 20,
  LU: 20,
  LV: 21,
  MC: 27,
  MD: 24,
  ME: 22,
  MK: 19,
  MR: 27,
  MT: 31,
  MU: 30,
  NL: 18,
  NO: 15,
  PK: 24,
  PL: 28,
  PS: 29,
  PT: 25,
  QA: 29,
  RO: 24,
  RS: 22,
  SA: 24,
  SC: 31,
  SE: 24,
  SI: 19,
  SK: 24,
  SM: 27,
  ST: 25,
  SV: 28,
  TL: 23,
  TN: 24,
  TR: 26,
  UA: 29,
  VA: 22,
  VG: 24,
  XK: 20
};
function isValidIban(value) {
  const iban = normalizeIban(value);
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/.test(iban)) return false;
  const expectedLength = IBAN_LENGTHS[iban.slice(0, 2)];
  if (expectedLength !== void 0 && iban.length !== expectedLength) return false;
  const rearranged = iban.slice(4) + iban.slice(0, 4);
  let remainder = 0;
  for (const ch of rearranged) {
    const digits = /[A-Z]/.test(ch) ? String(ch.charCodeAt(0) - 55) : ch;
    for (const d of digits) remainder = (remainder * 10 + Number(d)) % 97;
  }
  return remainder === 1;
}
function findIbans(text) {
  const found = [];
  const upper = text.toUpperCase();
  const pattern = /\b[A-Z]{2}\d{2}(?:[ ]?[A-Z0-9]{1,4}){3,8}\b/g;
  let match;
  while ((match = pattern.exec(upper)) !== null) {
    const groups = match[0].split(" ");
    let hit = null;
    let used = 0;
    for (let n = groups.length; n >= 1 && !hit; n--) {
      const candidate = normalizeIban(groups.slice(0, n).join(""));
      if (isValidIban(candidate)) {
        hit = candidate;
        used = groups.slice(0, n).join(" ").length;
      }
    }
    if (hit && !found.includes(hit)) found.push(hit);
    pattern.lastIndex = match.index + (hit ? used : Math.max(1, (groups[0] ?? "").length));
  }
  return found;
}

// src/validation/bank.ts
var bankClassificationSchema = z14.enum([
  "unknown",
  "business",
  "private_outlay",
  "reimbursement",
  "transfer",
  "deposit",
  "withdrawal",
  "tax_payment",
  "bank_fee",
  "payout_income",
  "debit_return",
  "private"
]);
var PARTNER_CLASSIFICATIONS = ["deposit", "withdrawal", "reimbursement", "private"];
var DOCUMENTLESS_CLASSIFICATIONS = [
  "transfer",
  "deposit",
  "withdrawal",
  "reimbursement",
  "tax_payment",
  "bank_fee",
  "payout_income",
  "debit_return",
  "private"
];
var bankDaySchema = z14.string().regex(/^\d{4}-\d{2}-\d{2}$/).refine((s) => !Number.isNaN(Date.parse(s)) && new Date(s).toISOString().slice(0, 10) === s, {
  message: "Ung\xFCltiges Datum"
});
var bankQuarterSchema = z14.string().regex(/^\d{4}-Q[1-4]$/, "Quartal im Format JJJJ-Qn");
var ibanSchema = z14.string().transform(normalizeIban).refine(isValidIban, { message: "Ung\xFCltige IBAN" });
var evidenceSchema = z14.string().trim().min(5).max(4e3);
var bankAllocationSchema = z14.object({
  kind: z14.enum(["invoice", "expense"]),
  documentId: uuidSchema,
  amountCents: z14.number().int().safe().refine((n) => n !== 0, { message: "Betrag darf nicht 0 sein" }),
  evidence: z14.string().trim().min(5).max(2e3)
});
var bankReviewBodySchema = z14.object({
  expectedVersion: z14.number().int().min(1),
  classification: bankClassificationSchema,
  evidence: evidenceSchema,
  allocations: z14.array(bankAllocationSchema).max(100),
  partnerId: uuidSchema.optional(),
  pairedTransactionId: uuidSchema.optional(),
  suggestionId: z14.string().min(1).max(128).optional()
}).superRefine((v, ctx) => {
  const needsPartner = PARTNER_CLASSIFICATIONS.includes(v.classification);
  if (needsPartner && !v.partnerId)
    ctx.addIssue({ code: "custom", path: ["partnerId"], message: "Gesellschafter erforderlich" });
  if (!needsPartner && v.partnerId)
    ctx.addIssue({
      code: "custom",
      path: ["partnerId"],
      message: "Gesellschafter nur bei Einlage, Entnahme, Erstattung oder Privat"
    });
  if (v.classification === "debit_return" && !v.pairedTransactionId)
    ctx.addIssue({
      code: "custom",
      path: ["pairedTransactionId"],
      message: "Gegenbuchung der R\xFCcklastschrift erforderlich"
    });
  if (v.classification !== "debit_return" && v.pairedTransactionId)
    ctx.addIssue({
      code: "custom",
      path: ["pairedTransactionId"],
      message: "Gegenbuchung nur bei R\xFCcklastschrift"
    });
  if (v.classification === "debit_return" && v.allocations.length)
    ctx.addIssue({
      code: "custom",
      path: ["allocations"],
      message: "R\xFCcklastschrift ohne Belegzuordnung"
    });
});
var bankBulkReviewBodySchema = z14.object({
  items: z14.array(
    z14.object({
      id: uuidSchema,
      expectedVersion: z14.number().int().min(1),
      suggestionId: z14.string().min(1).max(128)
    })
  ).min(1).max(100)
});
var bankRejectBodySchema = z14.object({
  suggestionId: z14.string().min(1).max(128),
  reason: z14.string().trim().min(3).max(2e3)
});
var tenantPartnerCreateSchema = z14.object({
  displayName: z14.string().trim().min(1).max(120),
  ibans: z14.array(ibanSchema).max(10).default([])
});
var tenantPartnerUpdateSchema = z14.object({
  expectedVersion: z14.number().int().min(1),
  displayName: z14.string().trim().min(1).max(120).optional(),
  ibans: z14.array(ibanSchema).max(10).optional(),
  active: z14.boolean().optional()
});
var ruleFields = {
  name: z14.string().trim().min(1).max(120),
  counterpartyIban: ibanSchema.optional(),
  counterpartyNameContains: z14.string().trim().min(2).max(120).optional(),
  referenceContains: z14.string().trim().min(2).max(120).optional(),
  direction: z14.enum(["in", "out", "any"]).default("any"),
  minCents: z14.number().int().min(0).optional(),
  maxCents: z14.number().int().min(0).optional(),
  outcomeClassification: bankClassificationSchema.exclude(["unknown", "debit_return"]).optional(),
  outcomePartnerId: uuidSchema.optional(),
  outcomeSupplierId: uuidSchema.optional(),
  outcomeCategoryId: uuidSchema.optional()
};
function checkRule(v, ctx, partial) {
  if (!partial && !v.counterpartyIban && !v.counterpartyNameContains && !v.referenceContains)
    ctx.addIssue({
      code: "custom",
      message: "Mindestens IBAN, Name oder Verwendungszweck angeben"
    });
  if (!partial && !v.outcomeClassification && !v.outcomeSupplierId)
    ctx.addIssue({
      code: "custom",
      message: "Klassifikation oder Lieferant angeben (Gesellschafter/Kategorie allein wirken nicht)"
    });
  if (v.minCents !== void 0 && v.maxCents !== void 0 && v.minCents > v.maxCents)
    ctx.addIssue({ code: "custom", path: ["maxCents"], message: "Maximum kleiner als Minimum" });
}
var bankRuleCreateSchema = z14.object({ ...ruleFields, createdFromTransactionId: uuidSchema.optional() }).superRefine((v, ctx) => checkRule(v, ctx, false));
var bankRuleUpdateSchema = z14.object({
  expectedVersion: z14.number().int().min(1),
  name: ruleFields.name.optional(),
  counterpartyIban: ruleFields.counterpartyIban.nullable(),
  counterpartyNameContains: ruleFields.counterpartyNameContains.nullable(),
  referenceContains: ruleFields.referenceContains.nullable(),
  direction: z14.enum(["in", "out", "any"]).optional(),
  minCents: ruleFields.minCents.nullable(),
  maxCents: ruleFields.maxCents.nullable(),
  outcomeClassification: ruleFields.outcomeClassification.nullable(),
  outcomePartnerId: ruleFields.outcomePartnerId.nullable(),
  outcomeSupplierId: ruleFields.outcomeSupplierId.nullable(),
  outcomeCategoryId: ruleFields.outcomeCategoryId.nullable(),
  active: z14.boolean().optional()
}).superRefine(
  (v, ctx) => checkRule(
    {
      minCents: v.minCents ?? void 0,
      maxCents: v.maxCents ?? void 0
    },
    ctx,
    true
  )
);
var bankDismissalCreateSchema = z14.object({
  counterpartyKey: z14.string().min(1).max(200).optional(),
  transactionId: uuidSchema.optional(),
  reason: z14.string().trim().min(3).max(2e3)
}).refine((v) => Boolean(v.counterpartyKey) !== Boolean(v.transactionId), {
  message: "Genau eine Gruppe oder eine Buchung angeben"
});
var periodRefine = (v) => v.from <= v.to && Date.parse(v.to) - Date.parse(v.from) <= 366 * 864e5;
var periodMessage = { message: "Zeitraum maximal ein Jahr; Beginn vor Ende" };
var bankTransactionsQuerySchema = z14.object({
  from: bankDaySchema,
  to: bankDaySchema,
  account: z14.string().optional(),
  status: z14.enum(["offen", "vorschlag", "bestaetigt", "ausgeblendet"]).optional(),
  minLevel: z14.enum(["hoch", "mittel", "niedrig"]).optional()
}).refine(periodRefine, periodMessage);
var bankCoverageQuerySchema = z14.object({ from: bankDaySchema, to: bankDaySchema }).refine((v) => v.from <= v.to, { message: "Beginn vor Ende" });
var bankIstQuarterQuerySchema = z14.object({
  quarter: bankQuarterSchema,
  account: z14.string().optional(),
  format: z14.enum(["json", "csv"]).default("json")
});
var bankRecurringQuerySchema = z14.object({
  from: bankDaySchema,
  to: bankDaySchema,
  includeDismissed: z14.enum(["true", "false"]).default("false").transform((v) => v === "true"),
  format: z14.enum(["json", "csv"]).default("json")
}).refine(periodRefine, periodMessage);

// src/utils/calculations.ts
function calculateLineTotal(quantity, unitPrice, discountType, discountValue) {
  const base = quantity * unitPrice;
  if (!discountType || discountValue === void 0 || discountValue === 0) {
    return round(base);
  }
  if (discountType === "percent") {
    return round(base * (1 - discountValue / 100));
  }
  return round(base - discountValue);
}
function calculateInvoiceTotals(items, vatRate, overallDiscountPercent = 0) {
  const subtotal = round(items.reduce((sum, item) => sum + item.lineTotal, 0));
  const overallDiscountAmount = round(subtotal * overallDiscountPercent / 100);
  const netTotal = round(subtotal - overallDiscountAmount);
  const vatAmount = round(netTotal * (vatRate / 100));
  const grossTotal = round(netTotal + vatAmount);
  return {
    subtotal,
    overallDiscountAmount,
    netTotal,
    vatAmount,
    grossTotal
  };
}
function calculateDiscountBreakdown(items, netTotal) {
  const subtotal = round(items.reduce(
    (sum, item) => sum + Number(item.lineTotal),
    0
  ));
  return { subtotal, discount: round(subtotal - Number(netTotal)) };
}
function calculateInterest(principalAmount, annualRate, daysOverdue) {
  const dailyRate = annualRate / 100 / 365;
  const interest = principalAmount * dailyRate * daysOverdue;
  return round(interest);
}
function calculateReminderTotal(originalAmount, reminderFee, interestAmount) {
  return round(originalAmount + reminderFee + interestAmount);
}
function round(value) {
  return Math.round(value * 100) / 100;
}
function daysBetween(startDate, endDate) {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffTime = Math.abs(end.getTime() - start.getTime());
  return Math.ceil(diffTime / (1e3 * 60 * 60 * 24));
}
function isOverdue(dueDate) {
  const due = new Date(dueDate);
  const today = /* @__PURE__ */ new Date();
  today.setHours(0, 0, 0, 0);
  due.setHours(0, 0, 0, 0);
  return due < today;
}

// src/utils/format.ts
function formatCurrency(amount) {
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR"
  }).format(amount);
}
function formatNumber(value, decimals = 2) {
  return new Intl.NumberFormat("de-DE", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(value);
}
function formatPaymentTerm(days) {
  if (days === 0) return "Sofort";
  return `${days} ${days === 1 ? "Tag" : "Tage"}`;
}
function formatDate(date) {
  const d = typeof date === "string" ? new Date(date) : date;
  return new Intl.DateTimeFormat("de-DE").format(d);
}
function formatDateTime(date) {
  const d = typeof date === "string" ? new Date(date) : date;
  return new Intl.DateTimeFormat("de-DE", {
    dateStyle: "medium",
    timeStyle: "short"
  }).format(d);
}
function formatRelativeTime(date) {
  const d = typeof date === "string" ? new Date(date) : date;
  const now = /* @__PURE__ */ new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffDays = Math.floor(diffMs / (1e3 * 60 * 60 * 24));
  const rtf = new Intl.RelativeTimeFormat("de-DE", { numeric: "auto" });
  if (diffDays === 0) {
    const diffHours = Math.floor(diffMs / (1e3 * 60 * 60));
    if (diffHours === 0) {
      const diffMinutes = Math.floor(diffMs / (1e3 * 60));
      return rtf.format(-diffMinutes, "minute");
    }
    return rtf.format(-diffHours, "hour");
  }
  if (diffDays < 30) {
    return rtf.format(-diffDays, "day");
  }
  if (diffDays < 365) {
    const diffMonths = Math.floor(diffDays / 30);
    return rtf.format(-diffMonths, "month");
  }
  const diffYears = Math.floor(diffDays / 365);
  return rtf.format(-diffYears, "year");
}
function getUnitTypeLabel(unitType, quantity) {
  const labels = {
    hours: "Std.",
    days: ["Tag", "Tage"],
    pieces: "Stk.",
    flat_rate: "pauschal",
    km: "km"
  };
  const label = labels[unitType];
  if (Array.isArray(label)) {
    return quantity === 1 ? label[0] : label[1];
  }
  return label;
}
function getUnitTypeFullName(unitType) {
  const names = {
    hours: "Stunden",
    days: "Tage",
    pieces: "St\xFCck",
    flat_rate: "Pauschal",
    km: "Kilometer"
  };
  return names[unitType];
}
function formatInvoiceNumber(year, sequence) {
  return `${year}-${String(sequence).padStart(4, "0")}`;
}
function formatCustomerNumber(sequence) {
  return `K-${String(sequence).padStart(5, "0")}`;
}
function formatContractNumber(year, sequence) {
  return `V-${year}-${String(sequence).padStart(4, "0")}`;
}
function formatEstimateNumber(year, sequence) {
  return `KV-${year}-${String(sequence).padStart(4, "0")}`;
}
function formatIBAN(iban) {
  return iban.replace(/(.{4})/g, "$1 ").trim();
}
function getQuarter(date) {
  let year;
  let month;
  if (typeof date === "string") {
    const calendarDate = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
    if (calendarDate) {
      year = Number(calendarDate[1]);
      month = Number(calendarDate[2]);
    } else {
      const d = new Date(date);
      year = d.getFullYear();
      month = d.getMonth() + 1;
    }
  } else {
    year = date.getFullYear();
    month = date.getMonth() + 1;
  }
  const quarter = Math.ceil(month / 3);
  return { quarter, year };
}
function formatQuarter(quarter, year) {
  return `Q${quarter} ${year}`;
}
function formatPhoneNumber(phone) {
  const cleaned = phone.replace(/[^\d+]/g, "");
  if (cleaned.startsWith("+49")) {
    const rest = cleaned.slice(3);
    if (rest.length >= 10) {
      return `+49 ${rest.slice(0, 3)} ${rest.slice(3, 6)} ${rest.slice(6)}`;
    }
  }
  return cleaned;
}

// src/utils/expenseRowStatus.ts
var LABELS = {
  "quartal-closed": "Quartal abgeschlossen",
  "ocr-failure": "OCR fehlgeschlagen",
  betragsabweichung: "Betragsabweichung",
  overdue: "\xDCberf\xE4llig",
  "missing-date": "Datum fehlt",
  "missing-fields": "Felder fehlen",
  paid: "Bezahlt",
  default: ""
};
var PRECEDENCE = [
  "quartal-closed",
  "ocr-failure",
  "betragsabweichung",
  "overdue",
  "missing-date",
  "missing-fields",
  "paid"
];
function toBerlinISODate(d) {
  return d.toLocaleDateString("en-CA", { timeZone: "Europe/Berlin" });
}
function toISODateString(s) {
  return /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10) : null;
}
function isOverdue2(dueDate, paymentDate, today) {
  if (paymentDate !== null) return false;
  if (!dueDate) return false;
  const dueISO = toISODateString(dueDate);
  if (dueISO === null) return false;
  const todayISO = toBerlinISODate(today);
  return dueISO < todayISO;
}
function isMissingFields(input) {
  if (input.supplierId === null) return true;
  if (input.categoryId === null) return true;
  const gross = typeof input.grossTotal === "number" ? input.grossTotal : Number(input.grossTotal);
  if (!Number.isFinite(gross) || gross === 0) return true;
  return false;
}
function getExpenseRowStatus(input) {
  const today = input.today ?? /* @__PURE__ */ new Date();
  const matches = {
    "quartal-closed": input.quartalClosed === true,
    "ocr-failure": input.processingPhase === "failed",
    betragsabweichung: input.girocodeAmountMismatch === true || input.erechnungAmountMismatch !== null,
    overdue: isOverdue2(input.dueDate, input.paymentDate, today),
    "missing-date": input.invoiceDate === null,
    "missing-fields": isMissingFields(input),
    paid: input.paymentDate !== null,
    default: false
  };
  const matched = PRECEDENCE.filter((s) => matches[s]);
  const primary = matched[0] ?? "default";
  const secondary = matched.slice(1);
  return {
    primary,
    secondary,
    label: LABELS[primary],
    testid: `expense-row-status-${primary}`
  };
}

// src/build-info.ts
var RELEASE_VERSION = "2.1.0";
function getBuildInfo(source = {}) {
  const channel = (source.channel || "local").replace(/[^a-zA-Z0-9-]/g, "-");
  const revision = source.revision || "unknown";
  const shortRevision = revision.slice(0, 7);
  const buildNumber = source.buildNumber?.replace(/[^a-zA-Z0-9.-]/g, "-") || null;
  const prerelease = channel === "main" ? "" : `-${channel}${buildNumber ? `.${buildNumber}` : ""}`;
  const metadata = channel === "main" && buildNumber ? `build.${buildNumber}.${shortRevision}` : shortRevision;
  return {
    version: `${RELEASE_VERSION}${prerelease}+${metadata}`,
    release: RELEASE_VERSION,
    revision,
    buildNumber,
    channel
  };
}
export {
  AddressType,
  BillingInterval,
  DOCUMENTLESS_CLASSIFICATIONS,
  DiscountType,
  EstimateStatus,
  InvoiceStatus,
  PARTNER_CLASSIFICATIONS,
  RELEASE_VERSION,
  UnitType,
  accountingClassSchema,
  addressIdParamSchema,
  addressTypeSchema,
  addressWithCustomerParamSchema,
  archiveCustomerSchema,
  assignCategorySchema,
  auditActionSchema,
  auditEntityParamSchema,
  auditEntityTypeSchema,
  auditIdParamSchema,
  auditListQuerySchema,
  bankAllocationSchema,
  bankBulkReviewBodySchema,
  bankClassificationSchema,
  bankCoverageQuerySchema,
  bankDaySchema,
  bankDismissalCreateSchema,
  bankIstQuarterQuerySchema,
  bankQuarterSchema,
  bankRecurringQuerySchema,
  bankRejectBodySchema,
  bankReviewBodySchema,
  bankRuleCreateSchema,
  bankRuleUpdateSchema,
  bankTransactionsQuerySchema,
  billingAddressSnapshotSchema,
  billingIntervalSchema,
  calculateDiscountBreakdown,
  calculateInterest,
  calculateInvoiceTotals,
  calculateLineTotal,
  calculateReminderTotal,
  cancelInvoiceSchema,
  categoryListQuerySchema,
  contractBillingIntervalLabels,
  contractBillingIntervalMonths,
  contractBillingIntervalSchema,
  contractItemInputSchema,
  contractStatusColors,
  contractStatusLabels,
  contractStatusSchema,
  contractWithRelationsSchema,
  createAddressSchema,
  createContractSchema,
  createCustomerSchema,
  createEstimateSchema,
  createExpenseCategorySchema,
  createExpenseSchema,
  createInvoiceDraftSchema,
  createInvoiceItemSchema,
  createInvoiceSchema,
  createReminderSchema,
  createSupplierSchema,
  customerAddressParamSchema,
  customerIdParamSchema,
  customerListQuerySchema,
  daysBetween,
  discountTypeSchema,
  estimateItemInputSchema,
  estimateStatusColors,
  estimateStatusLabels,
  estimateStatusSchema,
  expenseItemInputSchema,
  expenseListQuerySchema,
  expenseSourceSchema,
  expenseStatusSchema,
  expenseStatusTransitionSchema,
  expenseTypeSchema,
  findIbans,
  formatContractNumber,
  formatCurrency,
  formatCustomerNumber,
  formatDate,
  formatDateTime,
  formatEstimateNumber,
  formatIBAN,
  formatInvoiceNumber,
  formatNumber,
  formatPaymentTerm,
  formatPhoneNumber,
  formatQuarter,
  formatRelativeTime,
  getBuildInfo,
  getExpenseRowStatus,
  getQuarter,
  getUnitTypeFullName,
  getUnitTypeLabel,
  ibanSchema,
  invoiceIdParamSchema,
  invoiceItemIdParamSchema,
  invoiceListQuerySchema,
  invoiceReminderParamSchema,
  invoiceStatusSchema,
  isOverdue,
  isValidIban,
  isoDateSchema,
  isoDateTimeSchema,
  listContractsQuerySchema,
  listEstimatesQuerySchema,
  loginSchema,
  markInvoiceDeferredSchema,
  markInvoicePaidSchema,
  markInvoiceSentSchema,
  moneySchema,
  nonNegativeMoneySchema,
  normalizeIban,
  paginationQuerySchema,
  positiveMoneySchema,
  reminderIdParamSchema,
  reminderLevelConfigSchema,
  reminderListQuerySchema,
  reorderInvoiceItemsSchema,
  round,
  searchQuerySchema,
  supplierListQuerySchema,
  tenantPartnerCreateSchema,
  tenantPartnerUpdateSchema,
  unDeferInvoiceSchema,
  unitTypeSchema,
  updateAddressSchema,
  updateContractSchema,
  updateCustomerSchema,
  updateEstimateSchema,
  updateExpenseCategorySchema,
  updateExpenseSchema,
  updateInvoiceDraftSchema,
  updateInvoiceItemSchema,
  updateInvoiceNotesSchema,
  updateInvoicePaymentDateSchema,
  updateInvoiceSchema,
  updateReminderSchema,
  updateSupplierSchema,
  uuidSchema,
  vatRateSchema,
  versionSchema
};
//# sourceMappingURL=index.js.map